// Instrumente (Redesign Schritt 6, Spec 4.4): Suche, Markt-Chips, Datenstand, Aktiv-Schalter,
// Hinzufuegen mit Yahoo-Vorschau. Deaktivieren loescht die Signale des Symbols (Backend).
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { MARKET_LABEL, Market, marketOf } from "../scanner/markets";
import { Field, Sheet } from "../journal/TradeForm";

const PAGE = 100;
const STALE_DAYS = 4;   // Wochenende + Feiertag
type Chip = "ALL" | Market;
const CHIPS: Chip[] = ["ALL", "EU", "US", "ASIA", "CMD"];

const ddmm = (s: string | null) => s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.` : "–";
const staleDays = (s: string | null) => s ? (Date.now() - new Date(s + "T00:00:00").getTime()) / 864e5 : Infinity;

function Toggle({ on, label, busy, onChange }: { on: boolean; label: string; busy: boolean; onChange: () => void }) {
  return (
    <button role="switch" aria-checked={on} aria-label={label} className={`in-toggle${on ? " on" : ""}`} disabled={busy}
      onClick={e => { e.stopPropagation(); onChange(); }}><span /></button>
  );
}

function AddSheet({ exchanges, onClose, onAdded }: { exchanges: string[]; onClose: () => void; onAdded: (msg: string) => void }) {
  const [symbol, setSymbol] = useState("");
  const [pv, setPv] = useState<any>(null);
  const [exchange, setExchange] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const check = async () => {
    if (!symbol.trim()) return;
    setBusy(true); setErr(null); setPv(null);
    try {
      const r = await apiClient.get("/scanner/instruments/preview", { params: { symbol: symbol.trim() } });
      setPv(r.data); setExchange(r.data.exists?.exchange || r.data.suggested_exchange || ""); setName(r.data.exists?.name || r.data.name || "");
    } catch (e: any) { setErr(e?.response?.data?.detail || "Vorschau fehlgeschlagen"); }
    setBusy(false);
  };
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await apiClient.post("/scanner/instruments/add", null, { params: { symbol: pv.symbol, exchange, name } });
      const d = r.data || {};
      onAdded(d.message || `${pv.symbol} gespeichert`);
    } catch (e: any) { setErr(e?.response?.data?.detail || "Speichern fehlgeschlagen"); setBusy(false); }
  };
  const market = pv ? MARKET_LABEL[marketOf(exchange, null)] : "";
  return (
    <Sheet title="Instrument hinzufügen" onClose={onClose}
      footer={<>{err && <span className="jf-err">{err}</span>}
        <button className="jf-btn" onClick={onClose}>Abbrechen</button>
        <button className="jf-btn primary" disabled={!pv || busy || !exchange || (pv.exists && pv.exists.is_active)} onClick={save}>
          {busy && pv ? "Speichert …" : pv?.exists && !pv.exists.is_active ? "Reaktivieren" : "Speichern"}</button></>}>
      <div className="jf-grid">
        <Field label="Yahoo-Symbol" full>
          <div className="jf-inline">
            <input value={symbol} autoFocus placeholder="z. B. RHM.DE, AAPL, GC=F" autoCapitalize="characters"
              onChange={e => { setSymbol(e.target.value.toUpperCase()); setPv(null); }}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); check(); } }} />
            <button className="jf-btn" onClick={check} disabled={busy || !symbol.trim()}>{busy && !pv ? "…" : "Prüfen"}</button>
          </div>
        </Field>
        {pv && <>
          <div className="in-preview full">
            <div className="sc-company">{name || pv.name || pv.symbol}</div>
            <div className="sc-meta mono">{[exchange || "Börse?", pv.currency, market && `Markt ${market}`, pv.benchmark && `Index ${pv.benchmark}`].filter(Boolean).join(" · ")}</div>
            <div className={`sc-time ${pv.bars_1mo ? "" : "stale"}`}>
              {pv.bars_1mo ? `Yahoo liefert Kurse (${pv.bars_1mo} Tage im letzten Monat, letzter ${ddmm(pv.last_bar)})`
                : pv.error ? `Yahoo nicht erreichbar: ${pv.error}` : "Yahoo liefert für dieses Symbol keine Kurse"}</div>
            {pv.exists && <div className="sc-time stale">{pv.exists.is_active ? "Ist schon aktiv in der Liste." : "Ist vorhanden, aber inaktiv. Speichern reaktiviert es."}</div>}
            <div className="sc-time">Historie wird beim Speichern geladen (Daily ab 2016).</div>
          </div>
          <Field label="Börse"><select value={exchange} onChange={e => setExchange(e.target.value)}>
            <option value="">wählen</option>{exchanges.map(x => <option key={x} value={x}>{x}</option>)}</select></Field>
          <Field label="Name"><input value={name} onChange={e => setName(e.target.value)} /></Field>
        </>}
      </div>
    </Sheet>
  );
}

export default function InstrumentsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [chip, setChip] = useState<Chip>("ALL");
  const [onlyInactive, setOnlyInactive] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["instruments-all"],
    queryFn: () => apiClient.get("/scanner/instruments/all").then(r => r.data),
    staleTime: 5 * 60_000,
  });
  const all: any[] = Array.isArray(data?.instruments) ? data.instruments : [];
  const qq = q.trim().toLowerCase();
  const list = useMemo(() => all.filter(i =>
    (chip === "ALL" || marketOf(i.exchange, i.index_member) === chip) &&
    (!onlyInactive || !i.is_active) &&
    (!qq || i.symbol.toLowerCase().includes(qq) || (i.name || "").toLowerCase().includes(qq))), [all, chip, onlyInactive, qq]);
  const inactive = all.filter(i => !i.is_active).length;

  const flash = (t: string) => { setToast(t); window.setTimeout(() => setToast(null), 4000); };
  const toggle = async (i: any) => {
    if (i.is_active && !window.confirm(`${i.name || i.symbol} deaktivieren? Seine aktuellen Signale werden gelöscht.`)) return;
    setBusy(i.symbol);
    try {
      await apiClient.patch(`/scanner/instruments/${encodeURIComponent(i.symbol)}/active`, { active: !i.is_active });
      qc.setQueryData(["instruments-all"], (d: any) => d && { ...d, instruments: d.instruments.map((x: any) => x.symbol === i.symbol ? { ...x, is_active: !i.is_active } : x) });
      flash(`${i.symbol} ${i.is_active ? "deaktiviert" : "aktiviert"}`);
    } catch { flash("Ändern fehlgeschlagen"); }
    setBusy(null);
  };

  return (
    <>
      <input className="sc-search" placeholder="Instrument suchen (Name oder Symbol)" value={q}
        onChange={e => { setQ(e.target.value); setLimit(PAGE); }} aria-label="Instrument suchen" />
      <div className="sc-chips" style={{ paddingTop: 2 }}>
        {CHIPS.map(c => <button key={c} className={`sc-chip${chip === c ? " on" : ""}`} aria-pressed={chip === c}
          onClick={() => { setChip(c); setLimit(PAGE); }}>{c === "ALL" ? "Alle" : MARKET_LABEL[c]}</button>)}
        <button className={`sc-chip${onlyInactive ? " on" : ""}`} aria-pressed={onlyInactive} onClick={() => { setOnlyInactive(v => !v); setLimit(PAGE); }}>Inaktiv</button>
      </div>
      <div className="sc-count"><span>{list.length.toLocaleString("de-DE")} Instrumente · {inactive} inaktiv</span><span>Datenstand</span></div>
      <div className="j-list">
        {isLoading && <div className="sc-empty">Lädt …</div>}
        {isError && <div className="sc-empty">Instrumente konnten nicht geladen werden.</div>}
        {!isLoading && !isError && list.length === 0 && <div className="sc-empty">Nichts gefunden.</div>}
        {list.slice(0, limit).map(i => {
          const stale = i.is_active && staleDays(i.last_daily) > STALE_DAYS;
          return (
            <div key={i.symbol} className={`in-row${i.is_active ? "" : " off"}`}>
              <div className="sc-body">
                <div className="sc-name"><span className="sc-company">{i.name || i.symbol}</span><span className="sc-sym mono">{i.symbol}</span></div>
                <div className="sc-time">{i.exchange || "–"} · {MARKET_LABEL[marketOf(i.exchange, i.index_member)]}{i.currency ? ` · ${i.currency}` : ""}</div>
              </div>
              <span className={`mono in-data${stale ? " stale" : ""}`} title={i.last_daily ? `letzter Daily-Bar ${i.last_daily}` : "keine Daily-Bars"}>{ddmm(i.last_daily)}</span>
              <Toggle on={!!i.is_active} busy={busy === i.symbol} label={`${i.symbol} aktiv`} onChange={() => toggle(i)} />
            </div>
          );
        })}
      </div>
      {list.length > limit && <button className="au-more" onClick={() => setLimit(l => l + PAGE)}>Weitere {Math.min(PAGE, list.length - limit)} anzeigen</button>}
      {toast && <div className="sc-toast" role="status" style={{ marginTop: 12 }}><span>{toast}</span></div>}
      <button className="j-add" onClick={() => setAdding(true)}>+ Instrument hinzufügen</button>
      {adding && <AddSheet exchanges={data?.exchanges || []} onClose={() => setAdding(false)}
        onAdded={msg => { setAdding(false); flash(msg); qc.invalidateQueries({ queryKey: ["instruments-all"] }); }} />}
    </>
  );
}
